import type { FC, ReactElement, ReactNode } from "react"

import { motion } from "framer-motion"

import { buildCssClassName, withPressHandler } from "@/3514/utils"
import { type TEmptyAsyncCallback } from "@/shared/types/functions"

import { ESurveyStatus, type IParticipantSurveyModel as IModel } from "../api"

import type { THandleListItemClickFunction } from "./types"

import { SurveysDataListItemStatusBadge as Status } from "."

type TProps = Pick<IModel, "id" | "closeDate" | "title" | "accessCode"> & {
    status: ESurveyStatus | string
    handleClick: THandleListItemClickFunction
    handleHover: THandleListItemClickFunction
    tabIndex: 0 | undefined
}

const WarningIcon: ReactNode = (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="1" y="1" width="16" height="16" rx="8" stroke="#FD4D00" strokeWidth="2" />
        <path
            d="M8.44941 10.324L7.88541 4.54H10.1174L9.54141 10.324H8.44941ZM8.02941 13V11.08H9.97341V13H8.02941Z"
            fill="#FD4D00"
        />
    </svg>
)

const SurveysDataListItem: FC<TProps> = ({
    tabIndex,
    id,
    status,
    closeDate,
    title,
    accessCode,
    handleClick,
    handleHover
}: TProps): ReactElement => {
    const hadItemWarning: boolean = status === ESurveyStatus.InCompleted

    const onAction: ReturnType<typeof withPressHandler> = withPressHandler((): Promise<void> => handleClick(accessCode))

    const onHover: TEmptyAsyncCallback = (): Promise<void> => handleHover(accessCode)

    return (
        <motion.li
            key={id}
            variants={{ hidden: { opacity: 0, y: 10 }, visible: { opacity: 1, y: 0 } }}
            transition={{ duration: 0.25, ease: "easeOut", type: "tween" }}
            className="surveys__list__item"
            onKeyDown={onAction}
            onClick={onAction}
            onMouseOver={onHover}
            onFocus={onHover}
            {...(tabIndex === 0 ? { tabIndex } : {})}
        >
            <div className="surveys__list__item__title">
                {hadItemWarning && WarningIcon}
                <span className={buildCssClassName({ "font-extrabold": hadItemWarning })}>{title}</span>
            </div>
            <span className="surveys__list__item__date">{closeDate}</span>
            <Status status={status} />
        </motion.li>
    )
}

export { SurveysDataListItem, type TProps as TSurveysDataListItemProps }
